import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/Button";
import { Drawer } from "../ui/Overlay";
import { Notice } from "../ui/Notice";
import { DiffView } from "./DiffView";
import { useT } from "../../lib/i18n";
import { canon, diffBlocks } from "../../lib/autosave";
import { fetchVersions, fetchTalentNodesOf } from "../../lib/api";
import { vnum } from "../../lib/server-config";
import type { Snapshot } from "../../lib/version";

export const TALENT_BLOCKS = ["talentNodes"] as const;

export function TalentPageActions(props: {
  draftId: string; onlineId: string | null; revision: number; editable: boolean; blocked: boolean;
  snapshot: Snapshot; onLog: () => void;
  onRestore: (snap: Snapshot) => void;
}) {
  const t = useT();
  const current = useRef(props);
  current.current = props;
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const [preview, setPreview] = useState<{ online: Snapshot; draft: Snapshot; id: string; revision: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const load = async () => {
    setOpen(true); setBusy(true); setError(""); setPreview(null);
    try {
      // Resolve live now, rather than trusting the page's cached active version.
      const live = (await fetchVersions()).find((v) => v.active);
      if (!live) throw new Error();
      const id = `v${live.configVersion}`;
      const nodes = await fetchTalentNodesOf(live.configVersion);
      if (nodes.status !== "published") throw new Error();
      const online = { ...current.current.snapshot, talentNodes: nodes.nodes };
      if (alive.current) setPreview({ online, draft: structuredClone(current.current.snapshot), id, revision: current.current.revision });
    } catch { if (alive.current) setError(t("talentPage.error")); }
    finally { if (alive.current) setBusy(false); }
  };
  const restore = async () => {
    if (!preview) return;
    setBusy(true); setError("");
    try {
      const versions = await fetchVersions();
      if (!alive.current) return;
      const now = current.current;
      const draft = versions.find((v) => v.configVersion === vnum(now.draftId));
      const live = versions.find((v) => v.active);
      if (now.blocked || !now.editable || draft?.status !== "draft" || draft.revision !== preview.revision ||
          live?.configVersion !== vnum(preview.id) ||
          canon(TALENT_BLOCKS.map((k) => now.snapshot[k])) !== canon(TALENT_BLOCKS.map((k) => preview.draft[k]))) {
        setError(t("talentPage.changed")); return;
      }
      now.onRestore(structuredClone(preview.online));
      setOpen(false);
    } catch { if (alive.current) setError(t("talentPage.error")); }
    finally { if (alive.current) setBusy(false); }
  };
  const changes = preview ? diffBlocks(preview.draft as unknown as Record<string, unknown>, preview.online as unknown as Record<string, unknown>, TALENT_BLOCKS) : [];
  return <>
    <div className="mb-3 flex flex-wrap gap-2">
      <Button size="sm" onClick={props.onLog}>{t("talentPage.pageLog")}</Button>
      <Button size="sm" disabled={!props.onlineId || props.blocked || busy} onClick={() => { void load(); }}>{t("talentPage.pageCompare")}</Button>
    </div>
    {open && <Drawer title={t("talentPage.pageCompare")} width={900} onClose={() => { if (!busy) setOpen(false); }}
      extra={<Button tone="danger" disabled={busy || !preview || !changes.length || !props.editable || props.blocked} onClick={() => { void restore(); }}>{t("talentPage.restore")}</Button>}>
      <Notice tone="warning">{t("talentPage.hint", { draft: props.draftId, online: preview?.id ?? props.onlineId ?? "—" })}</Notice>
      {error && <div role="alert" className="my-3 text-danger">{error}</div>}
      {preview && <div className="mt-3"><DiffView left={preview.draft}
        right={{ ...preview.draft, talentNodes: preview.online.talentNodes }}
        leftLabel={props.draftId} rightLabel={t("ver.online", { id: preview.id })} /></div>}
    </Drawer>}
  </>;
}
